import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents } from '@/lib/mockData';

export function AdminEventDashboard() {
  const hasEvents = mockEvents.length > 0;

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Events</h1>
            <p className="text-muted-foreground mt-1">
              {hasEvents 
                ? `You have ${mockEvents.length} events` 
                : 'When you begin creating events, they will appear here.'
              }
            </p>
          </div>
          <Link to="/admin/events/new/basics">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              CREATE NEW EVENT
            </Button>
          </Link>
        </div>

        {!hasEvents ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative w-64 h-64 mb-8">
              {/* Decorative background */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full border-2 border-dashed border-border opacity-30"></div>
                <div className="absolute w-40 h-40 rounded-full border-2 border-dashed border-border opacity-20"></div>
                <div className="absolute w-32 h-32 rounded-full border-2 border-dashed border-border opacity-10"></div>
              </div>
              {/* Calendar illustration */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 bg-card rounded-2xl border border-border flex flex-col items-center justify-center shadow-xl">
                  <Calendar className="w-12 h-12 text-primary mb-2" />
                  <div className="w-8 h-1 bg-muted rounded-full"></div>
                  <div className="flex gap-1 mt-2">
                    <div className="w-2 h-2 bg-muted rounded-full"></div>
                    <div className="w-2 h-2 bg-muted rounded-full"></div>
                    <div className="w-2 h-2 bg-muted rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">No Event, yet</h2>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              Create your first event to get started. You can manage tickets, livestreams, and more.
            </p>
            <Link to="/admin/events/new/basics">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Event
              </Button>
            </Link>
          </div>
        ) : (
          /* Events Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockEvents.map(event => (
              <Link key={event.id} to={`/admin/events/${event.id}`}>
                <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all">
                  <div className="aspect-video relative">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        event.status === 'upcoming' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        {event.status === 'upcoming' ? 'Upcoming' : 'Past'}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-white mb-1">{event.title}</h3>
                    <p className="text-sm text-muted-foreground">{event.venue}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminEventDashboard;
